import React from 'react';
import { extractImageUrls } from './toolImages';
import { 
  Code,
  MessageSquare,
} from 'lucide-react';

/**
 * Convert backend agent type to user-friendly label
 * 
 * @param {string} agentType - Backend agent type identifier
 * @returns {string} User-friendly label
 */
export const getAgentTypeLabel = (agentType) => {
  switch(agentType) {
    case 'coding_agent': 
    case 'agent': return 'Code';
    default: return agentType || 'Unknown';
  }
};

/**
 * Get appropriate icon component for agent type
 * 
 * @param {string} agentType - Backend agent type identifier
 * @returns {React.ReactElement} Icon component
 */
export const getAgentTypeIcon = (agentType) => {
  switch(agentType) {
    case 'coding_agent':
    case 'agent': return <Code size={12} className="mr-1" />;
    default: return <MessageSquare size={12} className="mr-1" />;
  }
};

/**
 * Get appropriate CSS classes for agent type badge
 * 
 * @param {string} agentType - Backend agent type identifier
 * @returns {string} CSS class string
 */
export const getAgentTypeColor = (agentType) => {
  switch(agentType) {
    case 'coding_agent':
    case 'agent': return 'bg-brand-primary/20 text-brand-primary dark:bg-brand-primary/30 dark:text-brand-bright';
    default: return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300';
  }
};


/**
 * Converts Message model objects to frontend format
 * 
 * @param {Array} messages - Array of Message objects from the API
 * @param {string} agentType - The agent type to use for non-user messages
 * @returns {Array} - Array of message objects for frontend consumption
 */
export const convertMessagesToFrontend = (messages, agentType) => {
  if (!Array.isArray(messages)) return [];

  let outputMessages = [];
  const providerToolCallIdToExecutionId = new Map();

  for (const message of messages) {
    const timestamp = new Date(message.timestamp);
    
    // Check if this is a sub-agent message by looking for sub_agent_info on the message itself
    const subAgentInfo = message.sub_agent_info || null;
    
    // Handle message content (should be an array)
    if (Array.isArray(message.content)) {
      for (const [blockIndex, contentBlock] of message.content.entries()) {
        if (contentBlock.type === 'text') {
          outputMessages.push({
            sender: message.role === 'user' ? 'user' : 
                   (subAgentInfo ? subAgentInfo.agent_name : agentType),
            content: contentBlock.text,
            timestamp: timestamp,
            uuid: message.id || crypto.randomUUID(),
            lastUpdated: timestamp,
            isStreaming: false,
            message_type: 'text',
            tool_description: null,
            isSubAgent: !!subAgentInfo,
            subAgentInfo: subAgentInfo,
            agentType: subAgentInfo ? subAgentInfo.agent_name : agentType
          });
        } else if (contentBlock.type === 'thinking') {
          outputMessages.push({
            sender: subAgentInfo ? subAgentInfo.agent_name : agentType,
            content: contentBlock.thinking,
            timestamp: timestamp,
            uuid: `${message.id || crypto.randomUUID()}-thinking-${blockIndex}`,
            lastUpdated: timestamp,
            isStreaming: false,
            message_type: 'thinking',
            tool_description: null,
            isSubAgent: !!subAgentInfo,
            subAgentInfo: subAgentInfo,
            agentType: subAgentInfo ? subAgentInfo.agent_name : agentType
          });
        } else if (contentBlock.type === 'redacted_thinking') {
          outputMessages.push({
            sender: subAgentInfo ? subAgentInfo.agent_name : agentType,
            content: 'Redacted thinking',
            timestamp: timestamp,
            uuid: `${message.id || crypto.randomUUID()}-redacted-thinking-${blockIndex}`,
            lastUpdated: timestamp,
            isStreaming: false,
            message_type: 'thinking',
            tool_description: null,
            isSubAgent: !!subAgentInfo,
            subAgentInfo: subAgentInfo,
            agentType: subAgentInfo ? subAgentInfo.agent_name : agentType
          });
        } else if (contentBlock.type === 'tool_result') {
          const toolCallId =
            contentBlock.execution_id ||
            providerToolCallIdToExecutionId.get(contentBlock.tool_use_id) ||
            contentBlock.tool_use_id;
          // Handle tool results - mark as tool_result message type
          outputMessages.push({
            sender: subAgentInfo ? subAgentInfo.agent_name : agentType,
            content: contentBlock.content,
            timestamp: timestamp,
            uuid: message.id || crypto.randomUUID(),
            lastUpdated: timestamp,
            isStreaming: false,
            message_type: contentBlock.is_error ? 'tool_error' : 'tool_result',
            tool_description: contentBlock.name,
            tool_name: contentBlock.name,
            tool_call_id: toolCallId,
            images: extractImageUrls(contentBlock.content),
            is_error: contentBlock.is_error,
            isSubAgent: !!subAgentInfo,
            subAgentInfo: subAgentInfo,
            agentType: subAgentInfo ? subAgentInfo.agent_name : agentType
          });
        } else if (contentBlock.type === 'tool_call' || contentBlock.type === 'tool_use') {
          const toolCallId = contentBlock.execution_id || contentBlock.id;
          if (contentBlock.id && toolCallId) {
            providerToolCallIdToExecutionId.set(contentBlock.id, toolCallId);
          }
          // Handle tool calls - mark as tool_call message type
          outputMessages.push({
            sender: subAgentInfo ? subAgentInfo.agent_name : agentType,
            content: 'Calling ' + contentBlock.name,
            timestamp: timestamp,
            uuid: message.id || crypto.randomUUID(),
            lastUpdated: timestamp,
            isStreaming: false,
            message_type: 'tool_call',
            tool_description: contentBlock.name,
            tool_name: contentBlock.name,
            tool_call_id: toolCallId,
            isSubAgent: !!subAgentInfo,
            subAgentInfo: subAgentInfo,
            agentType: subAgentInfo ? subAgentInfo.agent_name : agentType
          });
        } else if (contentBlock.type === 'sub_agent_info') {
          // Skip sub_agent_info blocks as they're metadata, not content to display
          continue;
        }
      }
    }
  }
  
  return outputMessages;
};


/**
 * Converts an array of messages with 'role' property to an array with 'sender' property
 * @deprecated Use convertMessagesToFrontend instead for new Message model
 * 
 * @param {Array} messages - Array of message objects with 'role' property
 * @param {string} agentType - The agent type to use for 'assistant' role messages
 * @returns {Array} - Array of message objects with 'sender' property
 */
export const convertAgentMessageHistoryToFrontend = (messages, agentType) => {
  console.warn('convertAgentMessageHistoryToFrontend is deprecated, use convertMessagesToFrontend');
  
  if (!Array.isArray(messages)) return [];

  // Initialize an empty array to store the converted messages
  let outputMessages = [];

  // Loop over messages to validate and prepare them for conversion
  for (let i = 0; i < messages.length; i++) {
    const timestamp = new Date();

    if (typeof messages[i].content === 'string') {
        outputMessages.push({
            sender: messages[i].role === 'user' ? 'user' : agentType,
            content: messages[i].content,
            timestamp: timestamp,
            uuid: crypto.randomUUID(),
            lastUpdated: timestamp,
            isStreaming: false,
            message_type: 'text',
            tool_description: null
        })
    } else {
        for (let j = 0; j < messages[i].content.length; j++) {
            if (messages[i].content[j].type === 'text') {
                outputMessages.push({
                    sender: messages[i].role === 'user' ? 'user' : agentType,
                    content: messages[i].content[j].text,
                    timestamp: timestamp,
                    uuid: crypto.randomUUID(),
                    lastUpdated: timestamp,
                    isStreaming: false,
                    message_type: 'text',
                    tool_description: null
                })
            } else if (messages[i].content[j].type === 'tool_result') {
                outputMessages.push({
                    sender: agentType,
                    content: messages[i].content[j].content,
                    timestamp: timestamp,
                    uuid: crypto.randomUUID(),
                    lastUpdated: timestamp,
                    isStreaming: false,
                    message_type: 'tool',
                    tool_description: messages[i].content[j].name
                })
            }
        }
    }
  }
  
  return outputMessages;
};
